import type { JournalManifest, JournalPermissions } from '../types/journal';

/**
 * Whether sending a sheet posts at once: by the ERP its journal was made on, as its manifest records it
 * or else as `instanceDraftState` reads it, and for a sheet with no journal yet by the company's ERP. A
 * flag without a journal belongs to none, and is not read.
 */
export async function sheetPostsAtOnce(
  manifest: Pick<JournalManifest, 'instanceId' | 'erpHasDraftState'>,
  instanceDraftState: (instanceId: string) => Promise<boolean | null>,
  companyHasDraftState: boolean,
): Promise<boolean> {
  const instanceId = manifest.instanceId;
  const own = instanceId ? manifest.erpHasDraftState ?? await instanceDraftState(instanceId) : null;
  return !(own ?? companyHasDraftState);
}

/**
 * The connection a new journal is made on: the one the tab's rights name, so its sends go to the ERP the
 * Push button and its warning were worded for. Rights read while the company had no connection are read
 * once more, since one may have been made since. Rights that name none, from an older backend, leave it
 * to the company's first active connection.
 */
export async function connectionForNewJournal(
  rights: () => Pick<JournalPermissions, 'erpConnectionId'> | null,
  readRightsAgain: () => Promise<void>,
  firstActive: () => Promise<string | null>,
): Promise<string | null> {
  const named = rights()?.erpConnectionId;
  if (named === undefined) return firstActive();
  if (named !== null) return named;
  await readRightsAgain();
  return rights()?.erpConnectionId ?? null;
}
